// When the iOS app takes a newer SPA bundle. The plugin calls live behind @cntrl/native; this file decides.
//
// The manifest is a static file in the lane's own SPA artifact (scripts/live-update-bundle.mjs writes it at build),
// fetched from the lane the binary was built for. A strictly newer bundle that this binary can run is DOWNLOADED in
// the background, then switched to at the first natural boundary (PO, 2026-10-14) — never under the reader's eyes:
//   - a cold launch, before anything renders (`applyDownloadedUpdate`);
//   - the next PAGE CHANGE (`onNavigation`): the reader is leaving the page anyway, so the reload lands them on
//     the page they asked for (the plugin keeps the URL path: `keepUrlPathAfterReload`). It goes ahead even with
//     unsaved edits, so a wrong "unsaved" flag can never hold an update back;
//   - a return to the foreground after AWAY_SWITCH_MS in the background, unless something holds unsaved edits;
//   - Settings › Account's "Restart" (`switchToWaitingUpdate`).
// Waiting only for a cold launch was not enough: an app iOS keeps alive in the background might not switch for days.
// The one immediate switch is the API refusing the running bundle (426 client-outdated): `updateNow`.
//
// Versions are commit unix times (vite.config.ts), shared by CI's bundles and the SPA a binary ships with, so "newer"
// is a number comparison. A build stamped "0" — no git, or the test runner — never updates.
import { create } from "zustand";
import {
  applyBundle,
  downloadBundle,
  downloadedBundles,
  isIntact,
  liveUpdateSupported,
  onNativeBackground,
  onNativeResume,
  type DownloadedBundle,
  type LiveBundle,
} from "@cntrl/native";
import { hasUnsavedEdits } from "@cntrl/ui";
import { API_URL } from "../config/env.js";
import { BUNDLE_VERSION, nativeBuild } from "./clientVersion.js";

/** Where a lane publishes its iOS bundle. Under /assets because every edge already sends that prefix to the SPA. */
export const MANIFEST_PATH = "/assets/live-update/ios/manifest.json";

/** At most one manifest check per this window. Checks run at launch, on every return to the foreground and on every
 * navigation (tab switches included), so a deploy reaches an app in use within about a minute of the reader's next
 * tap. The manifest is a tiny uncached static file. */
const CHECK_INTERVAL_MS = 60 * 1000;

/** How long the app must have been in the background for a return to the foreground to switch to a waiting bundle. */
export const AWAY_SWITCH_MS = 10 * 60 * 1000;

/** Whether a newer bundle is downloaded and waiting to be switched to. Settings reads it for "Update ready". */
export const useLiveUpdateReady = create<{ ready: boolean }>(() => ({ ready: false }));

export interface LiveManifest {
  version: string;
  minNativeBuild: number;
  file: string;
  checksum: string;
  sessionKey: string;
}

/** The manifest, if `json` is one. A lane that publishes nothing, or something malformed, offers no update. */
export function parseManifest(json: unknown): LiveManifest | null {
  if (!json || typeof json !== "object") return null;
  const m = json as Record<string, unknown>;
  const ok =
    typeof m.version === "string" &&
    /^\d+$/.test(m.version) &&
    typeof m.minNativeBuild === "number" &&
    typeof m.file === "string" &&
    typeof m.checksum === "string" &&
    typeof m.sessionKey === "string";
  return ok
    ? {
        version: m.version as string,
        minNativeBuild: m.minNativeBuild as number,
        file: m.file as string,
        checksum: m.checksum as string,
        sessionKey: m.sessionKey as string,
      }
    : null;
}

/**
 * Whether to take `manifest`: `take` (strictly newer, and this binary is new enough), `binary-too-old` (newer, but
 * it needs a native build this one is not), or `none`.
 */
export function offerFor(manifest: LiveManifest, running: string, native: string | null): "take" | "binary-too-old" | "none" {
  if (running === "0" || Number(manifest.version) <= Number(running)) return "none";
  if (native === null || Number(native) < manifest.minNativeBuild) return "binary-too-old";
  return "take";
}

async function fetchOffer(): Promise<LiveBundle | null> {
  const url = new URL(MANIFEST_PATH, API_URL || window.location.origin);
  const response = await fetch(url, { cache: "no-store" });
  if (!response.ok) return null;
  const manifest = parseManifest(await response.json());
  if (!manifest || offerFor(manifest, BUNDLE_VERSION, await nativeBuild()) !== "take") return null;

  // A version that already failed on this phone (it never reported itself started, so the plugin rolled it back)
  // is not taken again — otherwise every launch would download, apply and roll back the same broken bundle.
  const failed = (await downloadedBundles()).some((b) => b.version === manifest.version && b.status === "error");
  if (failed) return null;

  return {
    version: manifest.version,
    url: new URL(manifest.file, url).toString(),
    checksum: manifest.checksum,
    sessionKey: manifest.sessionKey,
  };
}

let lastCheck = 0;

/** Look for a newer bundle and download it for the next cold launch. Quiet on every failure: the next check retries. */
export async function checkForLiveUpdate(now: number = Date.now()): Promise<void> {
  if (!liveUpdateSupported() || now - lastCheck < CHECK_INTERVAL_MS) return;
  lastCheck = now;
  try {
    const offer = await fetchOffer();
    if (offer) {
      await downloadBundle(offer);
      useLiveUpdateReady.setState({ ready: true });
    }
  } catch {
    lastCheck = 0; // a failed check (offline, a bad download) should not wait out the whole window
  }
}

/**
 * At the start of a cold launch: if a newer bundle is already downloaded, switch to it before anything renders. The
 * app reloads into it and this never resolves; it resolves `false` when there is nothing to switch to, and the
 * caller boots the app as normal.
 */
export async function applyDownloadedUpdate(): Promise<boolean> {
  return switchToWaitingUpdate();
}

/** The newest intact downloaded bundle that is newer than the running one, if any. */
async function newestWaiting(): Promise<DownloadedBundle | null> {
  if (!liveUpdateSupported() || BUNDLE_VERSION === "0") return null;
  return (
    (await downloadedBundles())
      .filter((b) => isIntact(b) && Number(b.version) > Number(BUNDLE_VERSION))
      .sort((a, b) => Number(b.version) - Number(a.version))[0] ?? null
  );
}

/**
 * Switch to the waiting bundle now: the app reloads into it (on the same page) and this never resolves. Resolves
 * `false` when nothing is waiting or the switch failed, and the app carries on with the bundle it has.
 */
export async function switchToWaitingUpdate(): Promise<boolean> {
  try {
    const newest = await newestWaiting();
    if (!newest) {
      useLiveUpdateReady.setState({ ready: false });
      return false;
    }
    await applyBundle(newest.id);
    return true;
  } catch {
    return false; // a failed switch must never stop the app on the bundle it has
  }
}

/**
 * The reader navigated. A move to another PAGE (path changed — a tab switch is one) reloads into a waiting bundle
 * there, ignoring unsaved edits; otherwise, or with nothing waiting, it is a chance to look for a newer bundle
 * (throttled). A change of query string alone never reloads.
 */
export function onNavigation(pathChanged: boolean): void {
  if (pathChanged && useLiveUpdateReady.getState().ready) {
    void switchToWaitingUpdate();
    return;
  }
  void checkForLiveUpdate();
}

/**
 * Switch to the newest bundle now — the API refused this one. On success the app reloads and this never resolves; it
 * resolves `false` when there is nothing this binary can take, and the caller sends the reader to the App Store.
 */
export async function updateNow(): Promise<boolean> {
  if (!liveUpdateSupported()) return false;
  const offer = await fetchOffer();
  if (!offer) return false;
  await applyBundle(await downloadBundle(offer));
  return true;
}

/**
 * Check at launch and on every return to the foreground; on a return after AWAY_SWITCH_MS away, switch to a waiting
 * bundle first unless something holds unsaved edits. Returns the unsubscribe.
 */
export function startLiveUpdates(now: () => number = Date.now): () => void {
  void checkForLiveUpdate();
  let backgroundedAt: number | null = null;
  const offBackground = onNativeBackground(() => {
    backgroundedAt = now();
  });
  const offResume = onNativeResume(() => {
    const away = backgroundedAt === null ? 0 : now() - backgroundedAt;
    backgroundedAt = null;
    if (away >= AWAY_SWITCH_MS && useLiveUpdateReady.getState().ready && !hasUnsavedEdits()) {
      void switchToWaitingUpdate();
      return;
    }
    void checkForLiveUpdate();
  });
  return () => {
    offBackground();
    offResume();
  };
}

/** Tests only. */
export function _resetLiveUpdateForTest(): void {
  lastCheck = 0;
  useLiveUpdateReady.setState({ ready: false });
}
